/**
 * @file contains helper functions for adding/removing members filters search param
 */
import { Location } from 'react-router-dom';

import {
  SEARCH_PARAM__FILTER__MEMBERS,
  SEARCH_PARAM__HAS_DUE_DATE,
} from '../../../constants/routing/searchParams';
import { User } from '../../../generated/graphql';
import { modifyUrlParams, removeParamsKeysFromUrl } from '../../helpers/router';

/**
 * Gets url that adds `?filterMembers={userId}` search param
 *
 * @param location Location object from the history
 * @param userId   Name of the section
 * @returns        A string in a url format
 */
export const getAddMembersFilterUrl = (
  location: Location,
  userId?: User['id'],
): string => {
  return modifyUrlParams(location, {
    params: { [SEARCH_PARAM__FILTER__MEMBERS]: userId },
    paramsToRemove: [SEARCH_PARAM__HAS_DUE_DATE],
  });
};

/**
 * Gets url that removes `?filterMembers={userId}` search param
 *
 * @param location Location object from the history
 * @returns        A string in a url format
 */
export const getRemoveMembersFilterUrl = (location: Location): string => {
  return removeParamsKeysFromUrl(location, SEARCH_PARAM__FILTER__MEMBERS);
};

/**
 * Get url to add/remove members filter
 *
 * @param location Location object from the history
 * @param isActive Whether the filter is active
 * @param userId   Id of the user
 * @returns        Url to add/remove members filter
 */
export const getFilterMembersUrl = (
  location: Location,
  isActive: boolean,
  userId?: User['id'],
): string => {
  return isActive
    ? getRemoveMembersFilterUrl(location)
    : getAddMembersFilterUrl(location, userId);
};

/**
 * Get url to add/remove hasDueDate filter
 *
 * @param location   Location object from the history
 * @param isActive   Whether the filter is active
 * @param hasDueDate Boolean value passed to the param
 * @returns          Url to add/remove hasDueDate filter
 */
export const getHasDueDateQuickFilterUrl = (
  location: Location,
  isActive: boolean,
  hasDueDate: boolean,
): string => {
  return isActive
    ? removeParamsKeysFromUrl(location, SEARCH_PARAM__HAS_DUE_DATE)
    : modifyUrlParams(location, {
        params: { [SEARCH_PARAM__HAS_DUE_DATE]: hasDueDate.toString() },
        paramsToRemove: [SEARCH_PARAM__FILTER__MEMBERS],
      });
};
